import { cn } from '@/lib/cn';
import { CoinMark } from './CoinMark';
import {
  formatPercent,
  formatPrice,
  instruments,
  useQuotes,
  useSparklines,
  type Quote,
} from './markets';

/**
 * The live price list.
 *
 * Real quotes, not a mock-up. The card says where they come from and how often
 * they update, because a price list that looks live and is not is worse than no
 * list at all on a page about betting on price movements.
 */
export function MarketsCard() {
  const { data: quotes, isLoading, isError, isRefetching, isPaused, dataUpdatedAt } = useQuotes();
  const sparklines = useSparklines();

  const bySymbol = new Map((quotes ?? []).map((quote) => [quote.symbol, quote]));

  return (
    <section className="moon-markets" aria-labelledby="moon-markets-title">
      <div className="moon-markets-heading">
        <div>
          <p className="moon-eyebrow">Fiyat akışı</p>
          <h2 id="moon-markets-title">Piyasalar</h2>
        </div>
        <span
          className={cn(
            'moon-market-status',
            (isError || isPaused) && 'moon-market-status-error',
            !isError && !isPaused && (isLoading || !quotes?.length) && 'moon-market-status-loading',
          )}
        >
          <span aria-hidden="true" />
          {isPaused
            ? 'Çevrimdışı'
            : isError
              ? 'Bağlantı yok'
              : isLoading
                ? 'Bağlanıyor'
                : quotes?.length
                  ? 'Güncel'
                  : 'Veri bekleniyor'}
        </span>
      </div>
      <div className="moon-market-columns" aria-hidden="true">
        <span>Varlık</span>
        <span>Son 24 saat</span>
        <span>Fiyat / Değişim</span>
      </div>
      <ul className="moon-market-list">
        {instruments.map((instrument) => (
          <Row
            key={instrument.symbol}
            instrument={instrument}
            quote={bySymbol.get(instrument.symbol)}
            spark={sparklines.get(instrument.symbol)}
            isLoading={isLoading}
          />
        ))}
      </ul>

      <p className="moon-market-source">
        <span
          aria-hidden
          className={cn(
            'mt-1 size-1.5 shrink-0 rounded-full',
            // The dot is a status, so it is never the only carrier of one: the
            // sentence beside it says the same thing in words.
            isError || isPaused
              ? 'bg-red-500'
              : isRefetching || isLoading || !quotes?.length
                ? 'bg-amber-400'
                : 'bg-emerald-500',
          )}
        />
        <span>
          {isPaused ? (
            'Çevrimdışısın. Bağlantı geri geldiğinde fiyatlar yenilenecek.'
          ) : isError ? (
            'Fiyat akışına şu anda ulaşılamıyor. Bağlantı kurulduğunda fiyatlar burada görünecek.'
          ) : (
            <>
              Fiyatlar Binance’ten, 15 saniyede bir güncelleniyor
              {dataUpdatedAt > 0
                ? ` (son: ${new Date(dataUpdatedAt).toLocaleTimeString('tr-TR')})`
                : ''}
              . Moon’daki fiyatlar farklı olabilir.
            </>
          )}
        </span>
      </p>
    </section>
  );
}

function Row({
  instrument,
  quote,
  spark,
  isLoading,
}: {
  instrument: (typeof instruments)[number];
  quote: Quote | undefined;
  spark: number[] | undefined;
  isLoading: boolean;
}) {
  const up = (quote?.changePercent ?? 0) >= 0;

  return (
    <li className="moon-market-row">
      <CoinMark code={instrument.code} className="size-9" />

      <span className="moon-market-identity">
        <span className="block text-sm font-semibold text-surface-100">{instrument.code}</span>
        <span className="block truncate text-xs text-surface-500">{instrument.name}</span>
      </span>

      {spark ? <Sparkline points={spark} up={up} /> : <span className="moon-market-spark" />}

      <span className="moon-market-price">
        {quote ? (
          <>
            <span className="block font-mono text-sm text-surface-100 tabular-nums">
              {formatPrice(quote.price)}
            </span>
            <span
              className={cn(
                'block font-mono text-xs tabular-nums',
                up ? 'text-emerald-400' : 'text-red-400',
              )}
            >
              {formatPercent(quote.changePercent)}
            </span>
          </>
        ) : (
          // No price rather than a stale or invented one. A dash is honest; a
          // number that is quietly wrong is the failure this whole card has to
          // avoid.
          <span className="block font-mono text-sm text-surface-600 tabular-nums">
            {isLoading ? '···' : '—'}
          </span>
        )}
      </span>
    </li>
  );
}

/**
 * A 24-hour line, drawn from the closes.
 *
 * Its own tiny SVG rather than a charting library: it is a polyline with no
 * axes, no tooltip and no legend, and the smallest chart library is larger than
 * this whole page.
 */
function Sparkline({ points, up }: { points: number[]; up: boolean }) {
  if (points.length < 2) {
    return <span className="moon-market-spark" />;
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  // A flat line would divide by zero and vanish; 1 keeps it centred instead.
  const range = max - min || 1;

  const path = points
    .map((value, index) => {
      const x = (index / (points.length - 1)) * 100;
      const y = 30 - ((value - min) / range) * 28 - 1;

      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg aria-hidden viewBox="0 0 100 30" preserveAspectRatio="none" className="moon-market-spark">
      <path
        d={path}
        fill="none"
        stroke={up ? '#34d399' : '#f87171'}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
